import type { Path, UseFormReturn } from "react-hook-form";

import { useState } from "react";
import { DropZone, FileTrigger } from "react-aria-components";

import type { MediaItem } from "@/components/media-preview-modal/mediaPreviewModal.types.ts";

import { Button } from "@/components/button";
import { Label } from "@/components/field";
import { MediaPreviewModal } from "@/components/media-preview-modal/index.tsx";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

import type { Asset, FileAsset, RequestFormData } from "../types.ts";

import { useAssetDownload } from "../hooks/useAssetBulkDownload.tsx";
import { useAssetSelection } from "../hooks/useAssetSelection.tsx";
import { useAssetsSection } from "../hooks/useAssetsSection.ts";
import { useLinkForm } from "../hooks/useLinkForm.tsx";
import { ALLOWED_EXTENSIONS, isFileAsset } from "../utils/uploadUtils.ts";
import { AssetDownloadButtons } from "./AssetsDownloadButtons.tsx";
import { DeleteAllAssetsModal } from "./DeleteAllAssetsModal.tsx";
import { InsertLinkForm } from "./InsertLinkForm.tsx";
import { UploadAssetsInput } from "./UploadAssetsInput.tsx";
import { UploadedAsset } from "./UploadedAsset.tsx";
import { UploadedLink } from "./UploadedLink.tsx";

type AssetsFormProps<TFormValues extends RequestFormData> = {
  field: Path<TFormValues>;
  formMethods: UseFormReturn<TFormValues>;
  title: string;
};

export function AssetsForm<TFormValues extends RequestFormData>({
  field,
  formMethods,
  title,
}: AssetsFormProps<TFormValues>) {
  const assets = (formMethods.watch(field) ?? []) as Asset[];
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>();

  const {
    clearErrors,
    control,
    handleSubmit,
    setError: setLinkFormError,
  } = useLinkForm(assets);

  const {
    error,
    handleDeleteAll,
    handleFileDelete,
    handleFileDrop,
    handleFileSelect,
    handleLinkAdd,
    handleLinkDelete,
    isDeletingAll,
  } = useAssetsSection<TFormValues>(
    field,
    assets,
    setLinkFormError,
    formMethods,
  );

  const { handleAssetSelectionChange, selectedAssets } = useAssetSelection();

  const { handleDownloadSelected } = useAssetDownload();

  const handleAssetClick = (assetId: string) => {
    setSelectedAssetId(assetId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const mediaItems: MediaItem[] = assets
    .filter((element): element is FileAsset => isFileAsset(element))
    .filter((asset) => asset.status === "success")
    .map((asset) => ({
      extension: getExtensionFromFilename(asset.file.name),
      id: asset.id,
      name: asset.file.name,
      url: asset.awsDownloadUrl || URL.createObjectURL(asset.file),
    }));

  return (
    <div className="flex flex-col gap-sm text-text-secondary @container">
      <Label>{title}</Label>
      <div className="flex flex-col gap-sm">
        <DropZone
          className={({ isDropTarget }) =>
            `group relative gap-xs rounded-xl border ${
              isDropTarget
                ? "border-2 border-border-brand"
                : "border-border-secondary"
            } bg-bg-primary px-3xl py-xl hover:bg-bg-primary-hover`
          }
          onDrop={handleFileDrop}
        >
          <FileTrigger
            acceptedFileTypes={ALLOWED_EXTENSIONS}
            allowsMultiple
            onSelect={handleFileSelect}
          >
            <Button className="outline-hidden" variant="unstyled">
              <UploadAssetsInput />
            </Button>
          </FileTrigger>
        </DropZone>

        {error && (
          <span className="text-sm text-text-error-primary">{error}</span>
        )}
        <InsertLinkForm
          clearErrors={clearErrors}
          control={control}
          onSubmit={handleSubmit(handleLinkAdd)}
        />
        {assets.length > 0 ? (
          <div className="flex flex-col gap-lg">
            <AssetDownloadButtons
              hasSelectedAssets={selectedAssets.length > 0}
              onDeleteAll={() => setIsDeleteAllModalOpen(true)}
              onDownloadAll={() => {
                handleDownloadSelected(
                  assets
                    .filter(
                      (asset) =>
                        isFileAsset(asset) && asset.status === "success",
                    )
                    .map((asset): number => asset.attachmentId!),
                );
              }}
              onDownloadSelected={() => handleDownloadSelected(selectedAssets)}
            />
            <div className="flex flex-col gap-lg">
              {assets.map((asset: Asset) =>
                isFileAsset(asset) ? (
                  <UploadedAsset
                    file={asset}
                    handleCheckboxChange={handleAssetSelectionChange}
                    isSelected={selectedAssets.includes(
                      asset.attachmentId ?? 0,
                    )}
                    key={asset.id}
                    onAssetClick={handleAssetClick}
                    onDelete={handleFileDelete}
                  />
                ) : (
                  <UploadedLink
                    key={asset.id}
                    link={asset}
                    onDelete={handleLinkDelete}
                  />
                ),
              )}
            </div>
          </div>
        ) : null}
      </div>

      {isModalOpen && mediaItems.length > 0 && (
        <MediaPreviewModal
          currentItemId={selectedAssetId}
          mediaItems={mediaItems}
          onClose={handleModalClose}
        />
      )}

      <DeleteAllAssetsModal
        isDeleting={isDeletingAll}
        isOpen={isDeleteAllModalOpen}
        onConfirm={async () => {
          await handleDeleteAll();
          setIsDeleteAllModalOpen(false);
        }}
        onOpenChange={setIsDeleteAllModalOpen}
      />
    </div>
  );
}
